import { hasUIDField, UIDContext } from "@prismicio/editor-fields"
import { useOnChange } from "@prismicio/editor-support/React"
import { Document as DocumentT } from "@prismicio/types-internal/lib/content"
import type { CustomType } from "@prismicio/types-internal/lib/customtypes"
import { useCallback, useContext, useMemo, useState } from "react"

import { getReleaseId } from "../app/services/release"
import { useCollaborationStorage } from "./collaboration/CollaborationRoom"
import { useCollaborationState } from "./collaboration/CollaborationStateProvider"
import { useIsLastRedoAction } from "./collaboration/HistoryProvider"
import { type DocumentData, type NewDocumentData } from "./documentData"
import { useDocumentHasLocalEdits } from "./DocumentHasLocalEditsProvider"
import { setGeneratedUID } from "./extractRelevantTextFromContent"

export function useDraft(initData: DocumentData): DocumentDraft {
  const { type, customType } = initData

  const { meta, version } =
    initData.type === "existing"
      ? initData
      : { meta: undefined, version: undefined }

  const metaId = meta?.id

  const collaborationStorage = useCollaborationStorage({
    status: version?.status ?? "unclassified",
  })
  const initDocument = getInitDocument()
  const [previousMetaId, setPreviousMetaId] = useState(metaId)
  const [nextAnimation, setNextAnimation] = useState<NextAnimation>()
  const [cancelSignal, setCancelSignal] = useState(0)

  function getInitDocument(): DocumentT {
    // Collaboration room data.
    if (collaborationStorage?.value?.document && type === "existing") {
      // The data returned by Liveblocks after passing through their LiveObject gives a type of readonly.
      // The type is still DocumentT but just readonly.
      // We need to cast it to DocumentT to be able to use it in the editor.
      return collaborationStorage.value.document as DocumentT
    }

    return initData.document
  }

  function getEditedAt() {
    if (isNewDocFromCopy(initData)) return new Date()

    if (initData.type === "existing" && initData.isFromInitialDraft)
      return new Date()

    return undefined
  }

  function getDocumentTags() {
    if (isNewDocFromCopy(initData)) return new Set(initData.asCopy.tags)

    return new Set(version?.tags ?? [])
  }

  const [draft, setDraft] = useState<Draft>({
    animationId: crypto.randomUUID(),
    contentId: crypto.randomUUID(),
    document: initDocument,
    documentTags: getDocumentTags(),
    editedAt: getEditedAt(),
  })

  const isLastRedoAction = useIsLastRedoAction()

  useOnChange(collaborationStorage?.value, (storageValue) => {
    if (!storageValue) return

    // This is just Liveblocks giving back what we just fed it, ~30ms later. Nothing to do.
    // This prevents a full rendering of the editor and losing all our reference optimizations (useEffect, useMemo, etc)
    // since the Liveblocks copy went through a full deserialization process and everything has new references.
    //
    // If the last action was a redo, we don't want to block the update.
    // As the contentId is expected to be the same since we are going back in time to the initial content.
    if (!isLastRedoAction && storageValue.contentId === draft.contentId) return

    // Update our local draft with the newest server side collaboration copy.
    setDraft((draft) => {
      return {
        ...draft,
        ...(storageValue.document
          ? // The data returned by Liveblocks after passing through their LiveObject gives a type of readonly.
            // The type is still DocumentT but just readonly.
            // We need to cast it to DocumentT to be able to use it in the editor.
            { document: storageValue.document as DocumentT }
          : {}),
        ...(storageValue.lastModifiedDate
          ? { editedAt: new Date(storageValue.lastModifiedDate) }
          : {}),
      }
    })
  })

  const { editedUID } = useContext(UIDContext)

  const isFromCopy = isNewDocFromCopy(initData)

  const edit = useCallback(
    (document: DocumentT) => {
      const newDocument = transformDocumentAfterEdit({
        document,
        customType,
        type,
        editedUID: editedUID.current,
        isFromCopy,
      })

      const newContentId = crypto.randomUUID()

      setDraft((prevDraft) => ({
        ...prevDraft,
        contentId: newContentId,
        editedAt: new Date(),
        document: newDocument,
      }))
      collaborationStorage?.updateAndMaybeRedirect({
        document: newDocument,
        contentId: newContentId,
      })
    },
    [customType, type, editedUID, isFromCopy, collaborationStorage],
  )

  const resetDraft = useCallback(
    (animate = false) =>
      setDraft((draft) => ({
        animationId: animate ? crypto.randomUUID() : draft.animationId,
        contentId: crypto.randomUUID(),
        document: initDocument,
        documentTags: new Set(version?.tags ?? []),
      })),
    [initDocument, version],
  )

  const animateNextChange = useCallback((toVersionId?: string) => {
    setNextAnimation({ toVersionId })
  }, [])

  const cancel = useCallback(() => {
    setCancelSignal((signal) => signal + 1)
  }, [])

  const metaIdChanged = metaId !== previousMetaId
  if (metaIdChanged) setPreviousMetaId(metaId)

  const releaseId = version ? getReleaseId(version) : undefined

  // Batch and only reset a single time.
  const resetDraftSignal = useMemo(
    () => [cancelSignal, version?.status, version?.version_id, releaseId],
    [cancelSignal, releaseId, version?.status, version?.version_id],
  )

  useOnChange(resetDraftSignal, () => {
    // When the meta.id changes, the version_id always changes as well and
    // it's a big enough change that we want to force the animation.
    // Typically this happen when navigating with the previous button, etc.
    resetDraft(Boolean(nextAnimation) || metaIdChanged)
    setNextAnimation(undefined)
  })

  const updateTags = useCallback(
    (newTags: Set<string>) =>
      setDraft((prevDraft) => ({
        ...prevDraft,
        editedAt: new Date(),
        documentTags: newTags,
      })),
    [],
  )

  useHasLocalEditsSync(draft)

  return useMemo(
    () => ({
      ...draft,
      documentTags: collaborationStorage?.value?.tags
        ? new Set(collaborationStorage.value.tags)
        : draft.documentTags,
      edit,
      cancel,
      nextAnimation,
      animateNextChange,
      updateTags,
    }),
    [
      draft,
      edit,
      cancel,
      nextAnimation,
      updateTags,
      animateNextChange,
      collaborationStorage,
    ],
  )
}

export interface DocumentDraft {
  // The draft id is used to keep a stable reference after each draft reset.
  // This helps run animations when a significant change occurred.
  animationId: string
  // Purely client side ID used to uniquely identify a content revision.
  // We use it to fast compare the content sent to Liveblocks with the one that immediately comes back from it.
  contentId: string
  document: DocumentT
  editedAt?: Date
  edit: (newDocument: DocumentT) => void
  documentTags: Set<string>
  nextAnimation: NextAnimation
  updateTags: (newTags: Set<string>) => void
  cancel: () => void
  animateNextChange: (toVersionId?: string) => void
}

type NextAnimation = { toVersionId?: string } | undefined

export interface Draft {
  animationId: string
  contentId: string
  editedAt?: Date
  document: DocumentT
  documentTags: Set<string>
}

type NewDocFromCopy = NewDocumentData & {
  asCopy: NonNullable<NewDocumentData["asCopy"]>
}

function isNewDocFromCopy(doc: DocumentData): doc is NewDocFromCopy {
  return doc.type === "new" && Boolean(doc.asCopy)
}

interface TransformDocumentAfterEditParams {
  type: "new" | "existing"
  document: DocumentT
  customType: CustomType
  editedUID: boolean
  isFromCopy: boolean
}

function transformDocumentAfterEdit(params: TransformDocumentAfterEditParams) {
  const { type, document, customType, editedUID, isFromCopy } = params

  const shouldGenerateUID =
    type === "new" && hasUIDField(customType) && !editedUID && !isFromCopy

  if (!shouldGenerateUID) return document

  return setGeneratedUID(document)
}

function useHasLocalEditsSync(draft: Draft) {
  const { setHasLocalEdits } = useDocumentHasLocalEdits()
  const collaborationState = useCollaborationState()

  // We only set hasLocalEdits to true when the collaboration is disabled.
  // This is to avoid users losing local changes when the collaboration is enabled.
  useOnChange(draft.editedAt, (editedAt) => {
    if (editedAt && !collaborationState.enabled) {
      setHasLocalEdits(true)
    }
  })
}
